import React, { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";

export const formatEgp = (value) =>
  new Intl.NumberFormat("en-EG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const valueAt = (row, dataIndex) => {
  if (dataIndex == null) return undefined;
  if (Array.isArray(dataIndex)) return dataIndex.reduce((current, key) => current?.[key], row);
  return row?.[dataIndex];
};

const columnKey = (column, index) =>
  column.key || (Array.isArray(column.dataIndex) ? column.dataIndex.join(".") : column.dataIndex) || `col-${index}`;

export const FoBadge = ({ children, tone = "n" }) => (
  <span className={`fo-badge fo-badge-${tone}`}>
    <i />
    {children}
  </span>
);

const TONE = {
  Paid: "g",
  Confirmed: "g",
  Applied: "g",
  Active: "g",
  Completed: "g",
  Approved: "g",
  Delivered: "g",
  Invoiced: "g",
  Settled: "g",
  "Fully Paid": "g",
  Reviewed: "b",
  Yes: "g",
  Revenue: "g",
  Sent: "b",
  Processing: "b",
  Prepared: "b",
  Created: "b",
  Pending: "a",
  "Pending Approval": "a",
  Due: "a",
  "Due Today": "a",
  "Due This Week": "a",
  Upcoming: "a",
  Trial: "a",
  Partial: "a",
  "Partially Paid": "a",
  Escalated: "a",
  Mixed: "a",
  COD: "a",
  Overdue: "r",
  Unpaid: "r",
  Failed: "r",
  Rejected: "r",
  Cancelled: "r",
  Returned: "r",
  "Returned to Chainz": "r",
  Expense: "r",
  void: "r",
  No: "n",
  Draft: "n",
  Inactive: "n",
  Archived: "n",
  System: "n",
  Custom: "b",
  fee: "b",
  approval: "a",
  payment: "g",
};

export const statusBadge = (value) => {
  const text = value == null || value === "" ? "—" : String(value);
  return <FoBadge tone={TONE[text] || "n"}>{text}</FoBadge>;
};

export const FoCard = ({ title, count, note, actions, children, className = "" }) => (
  <section className={`fo-card ${className}`.trim()}>
    {(title || count || actions) && (
      <div className="fo-card-head">
        <div>
          {title && <h2>{title}</h2>}
          {note && <p className="fo-note">{note}</p>}
        </div>
        {count && <span className="fo-count">{count}</span>}
        {actions && <div className="fo-card-actions">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);

export const FoChips = ({ options, value, onChange }) => (
  <div className="fo-chips">
    {options.map((option) => {
      const key = typeof option === "string" ? option : option.value;
      const label = typeof option === "string" ? option : option.label;
      const on = value === key;
      return (
        <button key={key} type="button" className={`fo-chip${on ? " on" : ""}`} onClick={() => onChange(key)}>
          {label}
        </button>
      );
    })}
  </div>
);

export const FoSearch = ({ value, onChange, placeholder }) => (
  <label className="fo-search-inline">
    <SearchIcon sx={{ fontSize: 16 }} />
    <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
  </label>
);

export const FoField = ({ label, hint, children, grow }) => (
  <div className={`fo-field${grow ? " grow" : ""}`}>
    {label && <span>{label}</span>}
    {children}
    {hint && <em>{hint}</em>}
  </div>
);

export const FoFilter = ({ children, onApply, applying, applyLabel = "Apply" }) => (
  <div className="fo-card fo-filter">
    {children}
    {onApply && (
      <button type="button" className="fo-btn fo-btn-primary" onClick={onApply} disabled={applying}>
        {applyLabel}
      </button>
    )}
  </div>
);

export const FoKpis = ({ items, columns }) => (
  <div className="fo-kpis" style={columns ? { gridTemplateColumns: columns } : undefined}>
    {items.map((item) => (
      <div key={item.label} className={`fo-kpi${item.hot ? " hot" : ""}`}>
        <div className="fo-kpi-label">
          <span className="fo-dot" />
          {item.label}
        </div>
        <strong>{item.value}</strong>
        {item.note && <em>{item.note}</em>}
      </div>
    ))}
  </div>
);

export const FoEmpty = ({ children }) => <div className="fo-empty">{children}</div>;

export const FoDialog = ({ open, title, onClose, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fo-dialog-back" onMouseDown={onClose}>
      <div
        className={`fo-dialog${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="fo-card-head">
          <h2>{title}</h2>
          <button type="button" className="fo-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const FoBars = ({ months }) => {
  const max = Math.max(1, ...(months || []).flatMap((month) => [Number(month.revenue) || 0, Number(month.expenses) || 0]));
  if (!months?.length) return <FoEmpty>No months in this range.</FoEmpty>;
  return (
    <div className="fo-bars" aria-label="Revenue and expenses">
      {months.map((month) => (
        <div key={month.month} className="fo-bar-col">
          <div className="fo-bar-pair">
            <i style={{ height: `${((Number(month.revenue) || 0) / max) * 100}%` }} title="Revenue" />
            <i className="exp" style={{ height: `${((Number(month.expenses) || 0) / max) * 100}%` }} title="Expenses" />
          </div>
          <span>{String(month.label || month.month).split(" ")[0]}</span>
        </div>
      ))}
    </div>
  );
};

export const FoTable = ({
  columns,
  dataSource,
  rowKey = "_id",
  loading = false,
  pagination,
  expandable,
  onChange,
  empty = "Nothing to show.",
  minWidth,
}) => {
  const [sort, setSort] = useState(null);
  const [innerPage, setInnerPage] = useState(1);
  const [innerSize, setInnerSize] = useState(pagination?.pageSize || 10);
  const [open, setOpen] = useState({});

  const pageSize = pagination?.pageSize || innerSize;
  const serverPaged = Boolean(pagination?.current);

  const sorted = useMemo(() => {
    const rows = dataSource || [];
    if (!sort || typeof sort.sorter !== "function") return rows;
    const copy = rows.slice();
    copy.sort((a, b) => {
      const result = sort.sorter(a, b);
      return sort.order === "descend" ? -result : result;
    });
    return copy;
  }, [dataSource, sort]);

  const total = pagination?.total ?? sorted.length;
  const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize) || 1);
  const page = serverPaged ? pagination.current : Math.min(innerPage, pageCount);
  const visible = pagination === false || serverPaged ? sorted : sorted.slice((page - 1) * pageSize, page * pageSize);

  const template = [
    expandable ? "36px" : null,
    ...columns.map((column) => (column.key === "actions" || column.title === "Actions" || column.title === "" ? "max-content" : "minmax(120px, 1fr)")),
  ]
    .filter(Boolean)
    .join(" ");

  const emitPage = (nextPage, nextSize) => {
    if (!serverPaged) {
      setInnerPage(nextPage);
      setInnerSize(nextSize);
    }
    pagination?.onChange?.(nextPage, nextSize);
    onChange?.({ current: nextPage, pageSize: nextSize }, {}, sort ? { field: sort.field, order: sort.order } : {});
  };

  const toggleSort = (column) => {
    if (!column.sorter) return;
    const field = Array.isArray(column.dataIndex) ? column.dataIndex[column.dataIndex.length - 1] : column.dataIndex;
    const order = sort?.field === field && sort.order === "ascend" ? "descend" : "ascend";
    if (typeof column.sorter === "function") {
      setSort({ field, order, sorter: column.sorter });
      if (!serverPaged) setInnerPage(1);
      return;
    }
    const next = { field, order, sorter: true };
    setSort(next);
    onChange?.({ current: page, pageSize }, {}, { field, order });
  };

  return (
    <div>
      <div className="fo-table-scroll">
        <div className="fo-grid-table" style={{ minWidth: minWidth || Math.max(720, columns.length * 140) }}>
          <div className="fo-grid-head" style={{ gridTemplateColumns: template }}>
            {expandable && <span />}
            {columns.map((column, index) => {
              const field = Array.isArray(column.dataIndex) ? column.dataIndex[column.dataIndex.length - 1] : column.dataIndex;
              const active = Boolean(sort) && field != null && sort.field === field;
              return (
                <button
                  key={columnKey(column, index)}
                  type="button"
                  className={column.sorter ? "sortable" : ""}
                  onClick={() => toggleSort(column)}
                >
                  {column.title}
                  {active ? (sort.order === "ascend" ? " ↑" : " ↓") : ""}
                </button>
              );
            })}
          </div>
          {loading && <FoEmpty>Loading…</FoEmpty>}
          {!loading && visible.length === 0 && <FoEmpty>{empty}</FoEmpty>}
          {!loading &&
            visible.map((record, index) => {
              const key = typeof rowKey === "function" ? rowKey(record) : record?.[rowKey] ?? index;
              const expanded = Boolean(open[key]);
              return (
                <React.Fragment key={key}>
                  <div
                    className={`fo-grid-row${expanded ? " open" : ""}${expandable ? " can-open" : ""}`}
                    style={{ gridTemplateColumns: template }}
                    onClick={
                      expandable
                        ? (event) => {
                            if (event.target.closest("button, a, input, select, textarea")) return;
                            setOpen((current) => ({ ...current, [key]: !current[key] }));
                          }
                        : undefined
                    }
                  >
                    {expandable && <span className="fo-expand">{expanded ? "–" : "+"}</span>}
                    {columns.map((column, columnIndex) => {
                      const raw = valueAt(record, column.dataIndex);
                      const content = column.render ? column.render(raw, record, index) : raw ?? "—";
                      return <div key={columnKey(column, columnIndex)}>{content}</div>;
                    })}
                  </div>
                  {expanded && expandable?.expandedRowRender && (
                    <div className="fo-grid-expand" onClick={(event) => event.stopPropagation()}>
                      {expandable.expandedRowRender(record)}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
        </div>
      </div>
      {pagination !== false && total > 0 && (
        <div className="fo-pager">
          <span>
            {total} total · page {page} of {pageCount}
          </span>
          <div>
            {pagination?.showSizeChanger !== false && (
              <select
                value={pageSize}
                onChange={(event) => emitPage(1, Number(event.target.value))}
                aria-label="Rows per page"
              >
                {[pageSize, 10, 20, 50, 100]
                  .filter((size, index, list) => list.indexOf(size) === index)
                  .sort((a, b) => a - b)
                  .map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
              </select>
            )}
            <button type="button" className="fo-btn" disabled={page <= 1} onClick={() => emitPage(page - 1, pageSize)}>
              Previous
            </button>
            <button type="button" className="fo-btn" disabled={page >= pageCount} onClick={() => emitPage(page + 1, pageSize)}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
